import { getAuth } from 'firebase/auth';

class VaultService {
    constructor() {
        this.API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    }

    /**
     * Get authentication token
     */
    async getAuthToken() {
        const auth = getAuth();
        if (auth.currentUser) {
            return await auth.currentUser.getIdToken();
        }
        return null;
    }

    /**
     * Upload file to vault with progress tracking and cancellation support
     * @param {File} file - File to upload
     * @param {Object} options - Upload options
     * @param {string} options.episodeId - Episode ID to associate with
     * @param {string[]} options.tags - Tags for the asset
     * @param {string} options.description - Description of the asset
     * @param {string} options.folder - Folder name for organization (e.g., 'LinkedIn', 'Twitter')
     * @param {function} options.onProgress - Progress callback with detailed stats
     * @param {AbortSignal} options.signal - AbortSignal for cancellation support
     * @returns {Promise<Object>} Upload result
     */
    async uploadFile(file, options = {}) {
        const { episodeId, tags, description, folder, onProgress, signal } = options;

        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            // Use XMLHttpRequest for progress tracking
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                const startTime = Date.now();
                let lastLoaded = 0;
                let lastTime = startTime;
                const speedHistory = [];
                const maxSpeedSamples = 10;

                // Handle abort signal
                const handleAbort = () => {
                    xhr.abort();
                    reject(new Error('Upload cancelled'));
                };

                // Listen for abort signal
                if (signal) {
                    if (signal.aborted) {
                        handleAbort();
                        return;
                    }
                    signal.addEventListener('abort', handleAbort);
                }

                // Track upload progress with detailed stats
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable && onProgress) {
                        const now = Date.now();
                        const progress = Math.round((event.loaded / event.total) * 100);

                        // Calculate current speed (bytes per second)
                        const timeDiff = (now - lastTime) / 1000; // seconds
                        const bytesDiff = event.loaded - lastLoaded;
                        const currentSpeed = timeDiff > 0 ? bytesDiff / timeDiff : 0;

                        // Add to speed history for smoothing
                        speedHistory.push(currentSpeed);
                        if (speedHistory.length > maxSpeedSamples) {
                            speedHistory.shift();
                        }

                        // Calculate average speed from history
                        const avgSpeed = speedHistory.reduce((a, b) => a + b, 0) / speedHistory.length;

                        // Calculate ETA
                        const remainingBytes = event.total - event.loaded;
                        const etaSeconds = avgSpeed > 0 ? remainingBytes / avgSpeed : 0;

                        // Calculate elapsed time
                        const elapsedSeconds = (now - startTime) / 1000;

                        // Call progress callback with detailed stats
                        onProgress({
                            progress,
                            loaded: event.loaded,
                            total: event.total,
                            speed: avgSpeed,
                            speedFormatted: this.formatSpeed(avgSpeed),
                            eta: etaSeconds,
                            etaFormatted: this.formatTime(etaSeconds),
                            elapsed: elapsedSeconds,
                            elapsedFormatted: this.formatTime(elapsedSeconds)
                        });

                        lastLoaded = event.loaded;
                        lastTime = now;
                    }
                });

                xhr.addEventListener('load', () => {
                    // Clean up abort listener
                    if (signal) {
                        signal.removeEventListener('abort', handleAbort);
                    }

                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            const response = JSON.parse(xhr.responseText);
                            resolve(response);
                        } catch (e) {
                            reject(new Error('Invalid response format'));
                        }
                    } else {
                        try {
                            const error = JSON.parse(xhr.responseText);
                            reject(new Error(error.error || 'Upload failed'));
                        } catch (e) {
                            reject(new Error('Upload failed'));
                        }
                    }
                });

                xhr.addEventListener('error', () => {
                    // Clean up abort listener
                    if (signal) {
                        signal.removeEventListener('abort', handleAbort);
                    }
                    reject(new Error('Network error during upload'));
                });

                xhr.addEventListener('abort', () => {
                    // Clean up abort listener
                    if (signal) {
                        signal.removeEventListener('abort', handleAbort);
                    }
                    reject(new Error('Upload cancelled'));
                });

                // Build form data
                const formData = new FormData();
                formData.append('file', file);
                if (episodeId) formData.append('episodeId', episodeId);
                if (tags) formData.append('tags', JSON.stringify(tags));
                if (description) formData.append('description', description);
                if (folder) formData.append('folder', folder);

                // Open and send request
                xhr.open('POST', `${this.API_BASE_URL}/api/vault/upload`);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                xhr.send(formData);
            });

        } catch (error) {
            throw error;
        }
    }

    /**
     * Format speed for display (bytes per second)
     * @param {number} bytesPerSecond - Speed in bytes per second
     * @returns {string} Formatted speed string
     */
    formatSpeed(bytesPerSecond) {
        if (bytesPerSecond === 0) return '0 B/s';
        const k = 1024;
        const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
        const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
        return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Format time for display (seconds to MM:SS or HH:MM:SS)
     * @param {number} seconds - Time in seconds
     * @returns {string} Formatted time string
     */
    formatTime(seconds) {
        if (!isFinite(seconds) || seconds < 0) return '--:--';

        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        if (hours > 0) {
            return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    /**
     * Upload text snippet to vault
     * @param {string} content - Text content to upload
     * @param {string} title - Title for the snippet
     * @param {string} episodeId - Episode ID to associate with
     * @param {string[]} tags - Tags for organization (e.g., ['linkedin', 'social-media'])
     * @param {string} description - Description of the asset
     * @param {string} folder - Folder name for organization (e.g., 'LinkedIn', 'Twitter')
     * @returns {Promise<Object>} Upload result
     */
    async uploadTextSnippet(content, title, episodeId = null, tags = [], description = '', folder = null) {
        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(`${this.API_BASE_URL}/api/vault/upload-text`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    content,
                    title,
                    episodeId,
                    tags,
                    description,
                    folder
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Upload failed');
            }

            return result;

        } catch (error) {
            throw error;
        }
    }

    /**
     * List vault assets
     * @param {Object} options - Query options
     * @param {string} options.assetType - Filter by asset type
     * @param {string} options.episodeId - Filter by episode ID
     * @param {string} options.folder - Filter by folder name
     * @param {number} options.limit - Max number of results
     * @returns {Promise<Object>} List result with assets array and folders list
     */
    async listAssets(options = {}) {
        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const params = new URLSearchParams();
            if (options.assetType) params.append('assetType', options.assetType);
            if (options.episodeId) params.append('episodeId', options.episodeId);
            if (options.folder) params.append('folder', options.folder);
            if (options.limit) params.append('limit', options.limit);

            const queryString = params.toString();
            const url = `${this.API_BASE_URL}/api/vault/assets${queryString ? '?' + queryString : ''}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to list assets');
            }

            return result;

        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete vault asset
     * @param {string} assetId - Asset ID to delete
     * @returns {Promise<Object>} Delete result
     */
    async deleteAsset(assetId) {
        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(`${this.API_BASE_URL}/api/vault/assets/${assetId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to delete asset');
            }

            return result;

        } catch (error) {
            throw error;
        }
    }

    /**
     * Update asset name/title
     * @param {string} assetId - Asset ID to update
     * @param {string} newName - New name for the asset
     * @returns {Promise<Object>} Update result
     */
    async updateAssetName(assetId, newName) {
        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(`${this.API_BASE_URL}/api/vault/assets/${assetId}/name`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: newName })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to update asset name');
            }

            return result;

        } catch (error) {
            throw error;
        }
    }

    /**
     * Get storage usage statistics
     * @returns {Promise<Object>} Stats result
     */
    async getStats() {
        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(`${this.API_BASE_URL}/api/vault/stats`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to get stats');
            }

            return result;

        } catch (error) {
            throw error;
        }
    }

    /**
     * Validate file before upload
     * @param {File} file - File to validate
     * @param {number} maxSizeMB - Maximum file size in MB (default 5120 = 5GB for video)
     * @returns {Object} Validation result
     */
    validateFile(file, maxSizeMB = 5120) {
        if (!file) {
            return { valid: false, error: 'No file selected' };
        }

        const allowedTypes = [
            // Audio
            'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/aac', 'audio/m4a', 'audio/x-m4a',
            // Video
            'video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska',
            // Image
            'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
            // Text
            'text/plain', 'text/markdown', 'text/html', 'text/csv',
            // Application documents
            'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];

        const isAllowed = allowedTypes.some(type =>
            file.type.startsWith(type.split('/')[0] + '/') || file.type === type
        );

        if (!isAllowed) {
            return { valid: false, error: `File type ${file.type} is not allowed` };
        }

        const maxSize = maxSizeMB * 1024 * 1024;
        if (file.size > maxSize) {
            return { valid: false, error: `File size must be less than ${this.formatFileSize(maxSize)}` };
        }

        return { valid: true };
    }

    /**
     * Format file size for display
     * @param {number} bytes - File size in bytes
     * @returns {string} Formatted size string
     */
    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Get asset type icon name
     * @param {string} assetType - Asset type
     * @returns {string} Icon name for lucide-react
     */
    getAssetTypeIcon(assetType) {
        const iconMap = {
            audio: 'Music',
            video: 'Video',
            image: 'Image',
            document: 'FileText',
            text: 'FileText',
            other: 'File'
        };
        return iconMap[assetType] || 'File';
    }

    /**
     * Upload transcript to vault
     * @param {Object} transcript - Transcript data
     * @param {string} transcript.text - Transcript text
     * @param {string} transcript.title - Transcript title
     * @param {number} transcript.duration - Audio duration
     * @param {string} transcript.sourceType - Source type (youtube, upload, etc.)
     * @param {string} transcript.sourceUrl - Source URL
     * @param {Object} options - Upload options
     * @param {string} options.episodeId - Episode ID to associate with
     * @param {string} options.episodeTitle - Episode title
     * @param {string} options.userId - User ID
     * @returns {Promise<Object>} Upload result
     */
    async uploadTranscript(transcript, options = {}) {
        const { episodeId, episodeTitle, userId } = options;

        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const title = transcript.title || `Transcript - ${episodeTitle || 'Episode'}`;

            const response = await fetch(`${this.API_BASE_URL}/api/vault/transcript`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    text: transcript.text,
                    title,
                    episodeId,
                    duration: transcript.duration,
                    sourceType: transcript.sourceType,
                    sourceUrl: transcript.sourceUrl,
                    wordCount: transcript.wordCount || transcript.text?.split(/\s+/).filter(Boolean).length || 0
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to save transcript to vault');
            }

            return result;

        } catch (error) {
            throw error;
        }
    }
}

export default new VaultService();
